import { type ReactNode, useCallback, useMemo } from "react";
import type { TaskListQuery } from "@common/contracts/task.contract";
import type { TaskView } from "@common/types/views/tasksView";
import type { View } from "@common/types/views/views";
import { hasNarrowedTaskView } from "../views/opinionatedViewEmptyState";
import { TaskListEmptyState } from "./TaskListEmptyState";
import {
    toSavedView,
    withBaseViewFilters,
    withSavedColumns,
} from "./taskListView";

interface UseTaskListViewArgs {
    /** What `useTaskViewState` renders: the saved view plus the list's additions. */
    view: TaskView;
    /** The view as saved, which the toolbar shows. */
    savedView: TaskView;
    onSavedViewChange: (view: View) => void;
    /** The picked view; null with none, when every filter is the user's. */
    baseView: View | null;
    onViewSelect: (view: TaskView) => void;
    search: string | undefined;
    setFilters: (
        filters: TaskListQuery | ((prev: TaskListQuery) => TaskListQuery),
    ) => void;
    /** An opinionated view's own empty state, which wins over the list's. */
    viewEmptyState: ReactNode;
}

/**
 * The view the task list renders, its column and density changes saved back
 * to the picked view, and what it shows with no tasks. Clear filters drops
 * the search and the filters the user added, keeping the picked view's own.
 */
export const useTaskListView = ({
    view,
    savedView,
    onSavedViewChange,
    baseView,
    onViewSelect,
    search,
    setFilters,
    viewEmptyState,
}: UseTaskListViewArgs) => {
    const listView = useMemo(
        () => withSavedColumns({ view, savedColumns: savedView.columns }),
        [view, savedView.columns],
    );
    const handleListViewChange = useCallback(
        (nextListView: View) => {
            const nextSavedView = toSavedView({
                savedView,
                listView,
                nextListView,
            });
            if (nextSavedView !== savedView) {
                onSavedViewChange(nextSavedView);
            }
        },
        [savedView, listView, onSavedViewChange],
    );
    const clearFilters = useCallback(() => {
        onViewSelect(withBaseViewFilters({ view: savedView, baseView }));
        setFilters((prev) => ({ ...prev, search: "" }));
    }, [onViewSelect, savedView, baseView, setFilters]);
    const emptyState = viewEmptyState ?? (
        <TaskListEmptyState
            hasActiveFilters={hasNarrowedTaskView({
                filters: savedView.filters,
                viewFilters: baseView?.filters ?? {},
                search,
            })}
            onClearFilters={clearFilters}
        />
    );

    return { listView, handleListViewChange, emptyState };
};
